import React from 'react';
import PropTypes from 'prop-types';

/**
 * MenuItem Component
 *
 * Renders a single menu item with an icon and text.
 * Applies a static active/inactive color scheme and triggers a click event.
 *
 * Props:
 * - copy: string - The text label for the menu item.
 * - IconComponent: React component - The icon to display.
 * - active: boolean - Indicates if the menu item is currently active.
 * - onClick: function - Callback function to handle click events.
 * - className: string - (Optional) Extra classes for this element.
 */
const MenuItem = React.memo(({ copy, IconComponent, active, onClick, className = '' }) => {
  const baseClasses = 'cursor-pointer flex items-center gap-4 py-4 p-6';

  const activeClasses = active ? 'text-menu-active font-semibold' : 'text-black';

  const containerClasses = `${baseClasses} ${activeClasses} ${className}`.trim();

  const iconClasses = active ? '' : 'text-menu-inactive';

  return (
    <>
      <div className={containerClasses} onClick={onClick}>
        <IconComponent className={iconClasses} />
        <span>{copy}</span>
      </div>
    </>
  );
});

MenuItem.propTypes = {
  copy: PropTypes.string.isRequired,
  IconComponent: PropTypes.elementType.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default MenuItem;
